import { Fragment, useState } from 'react';
import Modal from 'react-modal';
import { IoClose } from 'react-icons/io5';
import { Text } from '@mantine/core';
import { idCreator } from '../../utils/idCreator';
import {
  CloseStyle,
  Title,
  DetailsModalBodyStyle,
  DetailsHeaderStyle,
  ScoreRingStyle,
  StatusPillStyle,
  DetailGridStyle,
  DetailCardStyle,
  SectionHeaderStyle,
  VerdictSummaryStyle,
  VerdictPillStyle,
  EvidenceFilterMetaStyle,
  EvidenceListStyle,
  EvidenceItemStyle,
  EvidenceHeaderStyle,
  EvidenceBadgeGroupStyle,
  EvidenceCategoryBadgeStyle,
  FieldConfidenceBadgeStyle,
  EvidenceValueStyle,
  EvidenceReasonStyle,
  SourceReferenceStyle,
  SourceMatchedValueStyle,
  SourceTableStyle,
  SourceTableCellStyle,
  NoteItemStyle,
  detailsModalStyles
} from './styles';

/* The verifier's own vocabulary, in colour. Shared with the tables that open this, so a row's
   confidence and the verdicts inside it are never two different greens. */
export const verdictTones = {
  matched: {
    color: '#2F9E44',
    background: 'light-dark(#EBFBEE, var(--mantine-color-green-light))',
    border: 'light-dark(#B2F2BB, var(--mantine-color-green-light-hover))'
  },
  uncertain: {
    color: '#F08C00',
    background: 'light-dark(#FFF4E6, var(--mantine-color-orange-light))',
    border: 'light-dark(#FFD8A8, var(--mantine-color-orange-light-hover))'
  },
  mismatch: {
    color: '#E03131',
    background: 'light-dark(#FFF5F5, var(--mantine-color-red-light))',
    border: 'light-dark(#FFC9C9, var(--mantine-color-red-light-hover))'
  },
  missing_source: {
    color: 'light-dark(#5C5F66, var(--mantine-color-dark-2))',
    background: 'light-dark(#F1F3F5, var(--mantine-color-dark-6))',
    border: 'light-dark(#DEE2E6, var(--mantine-color-dark-4))'
  },
  verified: {
    color: '#2F9E44',
    background: 'light-dark(#EBFBEE, var(--mantine-color-green-light))',
    border: 'light-dark(#B2F2BB, var(--mantine-color-green-light-hover))'
  },
  needs_review: {
    color: '#F08C00',
    background: 'light-dark(#FFF4E6, var(--mantine-color-orange-light))',
    border: 'light-dark(#FFD8A8, var(--mantine-color-orange-light-hover))'
  },
  unverified: {
    color: 'light-dark(#5C5F66, var(--mantine-color-dark-2))',
    background: 'light-dark(#F1F3F5, var(--mantine-color-dark-6))',
    border: 'light-dark(#DEE2E6, var(--mantine-color-dark-4))'
  }
};

export const getScoreColor = score => {
  if (score >= 85) return '#2F9E44';
  if (score >= 70) return '#F08C00';
  return '#E03131';
};

const formatSourceReference = sourceReference => {
  if (!sourceReference) return '-';
  const sourceText = String(sourceReference);

  const xyMatch = sourceText.match(/^x:\s*(\d+),\s*y:\s*(\d+)$/i);
  if (xyMatch) {
    const [, column, row] = xyMatch;
    return `Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
  }

  const bareCoordinatesMatch = sourceText.match(/^(\d+)\s*,\s*(\d+)$/);
  if (bareCoordinatesMatch) {
    const [, column, row] = bareCoordinatesMatch;
    return `Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
  }

  const parenthesizedCoordinatesMatch = sourceText.match(/^\((\d+)\s*,\s*(\d+)\)$/);
  if (parenthesizedCoordinatesMatch) {
    const [, column, row] = parenthesizedCoordinatesMatch;
    return `Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
  }

  const rowColumnMatch = sourceText.match(/^row\s*(\d+)\s*,\s*col(?:umn)?\s*:?\s*(\d+)$/i);
  if (rowColumnMatch) {
    const [, row, column] = rowColumnMatch;
    return `Row ${Number(row)}, Column ${Number(column)}`;
  }

  const valueAtCoordinatesMatch = sourceText.match(/^(.*)\s+at\s+\((\d+),\s*(\d+)\)$/i);
  if (valueAtCoordinatesMatch) {
    const [, value, column, row] = valueAtCoordinatesMatch;
    return `${value} at Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
  }

  return sourceText
    .replace(/source_row_index:\s*(\d+)/gi, (_, row) => `Row ${Number(row) + 1}`)
    .replace(/\bx:\s*(\d+)\b/gi, (_, column) => `Column ${Number(column) + 1}`)
    .replace(/\by:\s*(\d+)\b/gi, (_, row) => `Row ${Number(row) + 1}`);
};

const parseCoordinateSourceReference = (sourceReference, extractedValue) => {
  if (!sourceReference) return null;
  const sourceText = String(sourceReference).trim();
  const xyMatch = sourceText.match(/^x:\s*(\d+),\s*y:\s*(\d+)$/i);
  const bareCoordinatesMatch = sourceText.match(/^(\d+)\s*,\s*(\d+)$/);
  const parenthesizedCoordinatesMatch = sourceText.match(/^\((\d+)\s*,\s*(\d+)\)$/);
  const rowColumnMatch = sourceText.match(/^row\s*(\d+)\s*,\s*col(?:umn)?\s*:?\s*(\d+)$/i);
  const coordinateMatch = xyMatch || bareCoordinatesMatch || parenthesizedCoordinatesMatch;

  if (rowColumnMatch) {
    const [, row, column] = rowColumnMatch;
    return {
      rowNumber: Number(row),
      columnNumber: Number(column),
      sourceValue: extractedValue || '-'
    };
  }

  if (!coordinateMatch) return null;

  const [, column, row] = coordinateMatch;
  return {
    rowNumber: Number(row) + 1,
    columnNumber: Number(column) + 1,
    sourceValue: extractedValue || '-'
  };
};

const parseCsvLine = line => {
  const values = [];
  let currentValue = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    const nextCharacter = line[index + 1];

    if (character === '"' && nextCharacter === '"' && inQuotes) {
      currentValue += '"';
      index += 1;
    } else if (character === '"') {
      inQuotes = !inQuotes;
    } else if (character === ',' && !inQuotes) {
      values.push(currentValue.trim());
      currentValue = '';
    } else {
      currentValue += character;
    }
  }

  values.push(currentValue.trim());
  return values;
};

const normalizeComparableValue = value =>
  String(value ?? '')
    .trim()
    .replace(/,/g, '')
    .toLowerCase();

const parseStructuredSourceReference = (sourceReference, extractedValue) => {
  if (!sourceReference) return null;
  const sourceText = String(sourceReference).trim();
  if (!sourceText.startsWith('source_row_index,')) return null;

  const lines = sourceText.split(/\r?\n/).filter(Boolean);
  let headerLine = lines[0];
  let rowLine = lines[1];

  if (!rowLine) {
    const singleLineMatch = sourceText.match(/^(source_row_index(?:,\d+)+)\s+(.+)$/);
    if (!singleLineMatch) return null;
    [, headerLine, rowLine] = singleLineMatch;
  }

  const headers = parseCsvLine(headerLine);
  const values = parseCsvLine(rowLine);
  if (headers[0] !== 'source_row_index' || values.length < 2) return null;

  const sourceRowNumber = Number(values[0]) + 1;
  const extractedComparableValue = normalizeComparableValue(extractedValue);
  const sourceCells = headers
    .slice(1)
    .map((header, index) => ({
      columnNumber: Number(header) + 1,
      value: values[index + 1]
    }))
    .filter(cell => cell.value && cell.value.toLowerCase() !== 'nan');

  const matchedCells = sourceCells.filter(cell => {
    const cellComparableValue = normalizeComparableValue(cell.value);
    if (cellComparableValue === extractedComparableValue) return true;

    const numericCellValue = Number(cellComparableValue);
    const numericExtractedValue = Number(extractedComparableValue);
    return (
      Number.isFinite(numericCellValue) &&
      Number.isFinite(numericExtractedValue) &&
      Math.abs(numericCellValue - numericExtractedValue) < 0.000001
    );
  });

  const visibleCells =
    matchedCells.length > 0
      ? [...matchedCells, ...sourceCells.filter(cell => !matchedCells.includes(cell)).slice(0, 5)]
      : sourceCells.slice(0, 8);

  return {
    sourceRowNumber,
    matchedCells,
    visibleCells
  };
};

const parseCoordinateListSourceReference = (sourceReference, extractedValue) => {
  if (!sourceReference) return null;
  const sourceText = String(sourceReference).trim();
  const rowMatch = sourceText.match(/\by\s*=\s*(\d+)\b/i);
  if (!rowMatch || !sourceText.match(/\bx\s*=\s*\d+/i)) return null;

  const sourceRowNumber = Number(rowMatch[1]) + 1;
  const sourceCells = [];
  const xValueRegex = /\bx\s*=\s*(\d+)\s+([^,;]+)/gi;
  let match = xValueRegex.exec(sourceText);

  while (match) {
    sourceCells.push({
      columnNumber: Number(match[1]) + 1,
      value: match[2].trim()
    });
    match = xValueRegex.exec(sourceText);
  }

  if (!sourceCells.length) return null;

  const extractedComparableValue = normalizeComparableValue(extractedValue);
  const matchedCells = sourceCells.filter(cell => {
    const cellComparableValue = normalizeComparableValue(cell.value);
    if (cellComparableValue && extractedComparableValue.includes(cellComparableValue)) return true;

    const numericCellValue = Number(cellComparableValue);
    return (
      Number.isFinite(numericCellValue) &&
      extractedComparableValue.includes(String(numericCellValue))
    );
  });

  return {
    sourceRowNumber,
    matchedCells,
    visibleCells: sourceCells.slice(0, 10)
  };
};

const parseMultiColumnSourceReference = sourceReference => {
  if (!sourceReference) return null;
  const sourceText = String(sourceReference).trim();
  const match = sourceText.match(
    /^row\s+(\d+)\s*,\s*columns?\s+([\w\s/,-]+?)\s*,\s*source values?\s*:\s*(.+)$/i
  );
  if (!match) return null;

  const [, row, columnsText, valuesText] = match;
  const rawColumns = columnsText
    .split('/')
    .map(column => column.trim())
    .filter(Boolean);
  const values = valuesText
    .split(';')
    .map(value => value.trim())
    .filter(Boolean);
  const hasZeroBasedNumericColumn = rawColumns.some(column => Number(column) === 0);
  const visibleCells = rawColumns.map((column, index) => {
    const numericColumn = Number(column);
    const columnNumber = Number.isFinite(numericColumn)
      ? numericColumn + (hasZeroBasedNumericColumn ? 1 : 0)
      : column;

    return {
      columnNumber,
      value: values[index] ?? '-'
    };
  });

  return {
    sourceRowNumber: Number(row),
    matchedCells: visibleCells,
    visibleCells
  };
};

const renderStructuredSourceTable = (structuredSource, evidenceItemId) => (
  <SourceReferenceStyle id={`ai-extraction-evidence-source-structured-${evidenceItemId}`}>
    {structuredSource.matchedCells.length > 0 && (
      <SourceMatchedValueStyle id={`ai-extraction-evidence-source-match-${evidenceItemId}`}>
        Source value: {structuredSource.matchedCells[0].value} · Row{' '}
        {structuredSource.sourceRowNumber}, Column {structuredSource.matchedCells[0].columnNumber}
      </SourceMatchedValueStyle>
    )}
    <SourceTableStyle id={`ai-extraction-evidence-source-table-${evidenceItemId}`}>
      <SourceTableCellStyle
        id={`ai-extraction-evidence-source-row-header-${evidenceItemId}`}
        $header
      >
        Row
      </SourceTableCellStyle>
      <SourceTableCellStyle
        id={`ai-extraction-evidence-source-column-header-${evidenceItemId}`}
        $header
      >
        Column
      </SourceTableCellStyle>
      <SourceTableCellStyle
        id={`ai-extraction-evidence-source-value-header-${evidenceItemId}`}
        $header
      >
        Source value
      </SourceTableCellStyle>
      {structuredSource.visibleCells.map(cell => (
        <Fragment key={`${structuredSource.sourceRowNumber}-${cell.columnNumber}-${cell.value}`}>
          <SourceTableCellStyle
            id={`ai-extraction-evidence-source-row-${evidenceItemId}-${structuredSource.sourceRowNumber}-${cell.columnNumber}`}
          >
            {structuredSource.sourceRowNumber}
          </SourceTableCellStyle>
          <SourceTableCellStyle
            id={`ai-extraction-evidence-source-column-${evidenceItemId}-${structuredSource.sourceRowNumber}-${cell.columnNumber}`}
          >
            {cell.columnNumber}
          </SourceTableCellStyle>
          <SourceTableCellStyle
            id={`ai-extraction-evidence-source-value-${evidenceItemId}-${structuredSource.sourceRowNumber}-${cell.columnNumber}`}
          >
            {cell.value}
          </SourceTableCellStyle>
        </Fragment>
      ))}
    </SourceTableStyle>
  </SourceReferenceStyle>
);

const renderSourceReference = (item, evidenceItemId) => {
  const structuredSource = parseStructuredSourceReference(
    item.source_reference,
    item.extracted_value
  );
  const coordinateListSource = parseCoordinateListSourceReference(
    item.source_reference,
    item.extracted_value
  );
  const multiColumnSource = parseMultiColumnSourceReference(item.source_reference);
  const coordinateSource = parseCoordinateSourceReference(
    item.source_reference,
    item.extracted_value
  );
  if (coordinateSource) {
    return (
      <SourceReferenceStyle id={`ai-extraction-evidence-source-coordinate-${evidenceItemId}`}>
        <SourceMatchedValueStyle
          id={`ai-extraction-evidence-source-coordinate-match-${evidenceItemId}`}
        >
          Source value: {coordinateSource.sourceValue} · Row {coordinateSource.rowNumber}, Column{' '}
          {coordinateSource.columnNumber}
        </SourceMatchedValueStyle>
      </SourceReferenceStyle>
    );
  }

  if (coordinateListSource) {
    return renderStructuredSourceTable(coordinateListSource, evidenceItemId);
  }

  if (multiColumnSource) {
    return renderStructuredSourceTable(multiColumnSource, evidenceItemId);
  }

  if (!structuredSource) {
    return formatSourceReference(item.source_reference);
  }

  return renderStructuredSourceTable(structuredSource, evidenceItemId);
};

const getFieldConfidenceScore = item => {
  if (typeof item.confidence_score === 'number')
    return Math.min(100, Math.max(0, item.confidence_score));

  const verdictScores = {
    matched: 98,
    uncertain: 72,
    missing_source: 55,
    mismatch: 25
  };
  return verdictScores[item.verdict] ?? 55;
};

const sanitizeVerifierText = value => {
  if (!value) return '';
  const blockedPhrases = [
    'truncated',
    'json',
    'payload',
    'prompt',
    'coordinate',
    'coordinates',
    'schema',
    'compact extracted json',
    'provided source/extracted context',
    'provided source context',
    'provided context',
    'extraction payload shown',
    'later extracted sections',
    'later sections',
    'visible excerpt',
    'visible text'
  ];
  const sentences = String(value)
    .replace(/\n/g, ' ')
    .split('.')
    .map(sentence => sentence.trim())
    .filter(
      sentence =>
        sentence && !blockedPhrases.some(phrase => sentence.toLowerCase().includes(phrase))
    );

  return sentences.length ? `${sentences.join('. ')}.` : '';
};

const isMeaningfulEvidenceItem = item => {
  const field = String(item.field || '').toLowerCase();
  const extractedValue = String(item.extracted_value || '').toLowerCase();
  if (field.startsWith('completeness') || field.includes('full extraction after truncation'))
    return false;
  if (
    extractedValue.includes('not present in extracted json') ||
    extractedValue.includes('not fully available')
  ) {
    return false;
  }
  return true;
};

/**
 * What the verifier found, for one sheet.
 *
 * The same window over both extraction tables — the automated one and the one a first period is set
 * up by hand — because the thing being explained is the same thing: a score, and the field-level
 * evidence behind it. It reads everything out of the record's `ai_extraction_details`, so a caller
 * only has to say which row was clicked.
 */
export const AiExtractionDetails = ({ record, score = null, onClose }) => {
  /* Which verdict the reader has narrowed to. It belongs here rather than to the table: the filter
     has no meaning once this window is shut, and unmounting is a cleaner reset than remembering. */
  const [selectedVerdict, setSelectedVerdict] = useState(null);

  const details = record?.ai_extraction_details ?? {};
  const evidence = Array.isArray(details.evidence)
    ? details.evidence.filter(isMeaningfulEvidenceItem)
    : [];
  const concerns = Array.isArray(details.concerns)
    ? details.concerns.map(sanitizeVerifierText).filter(Boolean)
    : [];
  const methodology = Array.isArray(details.methodology)
    ? details.methodology.map(sanitizeVerifierText).filter(Boolean)
    : [];
  /* Every field matched and nothing left to review is a verdict of its own; the verifier's raw
     status does not say it. */
  const displayedStatus =
    evidence.length > 0 &&
    Number(details.needs_review_fields || 0) === 0 &&
    evidence.every(item => item.verdict === 'matched')
      ? 'verified'
      : details.status;
  const statusTone = verdictTones[displayedStatus] || verdictTones.unverified;
  const scoreColor = getScoreColor(score ?? 0);

  const getEvidenceItemId = item =>
    idCreator(
      [
        item.extraction_category || details.source?.category || record?.category,
        item.field,
        item.source_reference,
        item.verdict
      ]
        .filter(Boolean)
        .join('-')
    );

  /* Worst first: a reader opens this to find what went wrong, not to confirm what went right. */
  const sortedEvidence = [...evidence].sort((left, right) => {
    const priority = { mismatch: 0, uncertain: 1, missing_source: 2, matched: 3 };
    return (priority[left.verdict] ?? 4) - (priority[right.verdict] ?? 4);
  });
  const filteredEvidence = selectedVerdict
    ? sortedEvidence.filter(item => (item.verdict || 'missing_source') === selectedVerdict)
    : sortedEvidence;
  const verdictCounts = evidence.reduce((acc, item) => {
    const verdict = item.verdict || 'missing_source';
    acc[verdict] = (acc[verdict] || 0) + 1;
    return acc;
  }, {});

  const close = () => {
    setSelectedVerdict(null);
    onClose();
  };

  return (
    <Modal isOpen={!!record} onRequestClose={close} style={detailsModalStyles}>
      <DetailsModalBodyStyle id="ai-extraction-details-modal">
        <CloseStyle
          id="ai-extraction-details-close"
          as="button"
          type="button"
          aria-label="close AI extraction details"
          onClick={close}
        >
          <IoClose fontSize="18px" color="light-dark(#868E96, var(--mantine-color-dark-2))" />
        </CloseStyle>
        <Title id="ai-extraction-details-title">AI Extraction Details</Title>
        <DetailsHeaderStyle id="ai-extraction-details-header">
          <ScoreRingStyle
            id="ai-extraction-confidence-ring"
            $score={score ?? 0}
            $color={scoreColor}
          >
            <Text id="ai-extraction-confidence-value" fz="24px" lh="28px" fw={800} c={scoreColor}>
              {score == null ? '-' : `${score.toFixed(1)}%`}
            </Text>
            <Text
              id="ai-extraction-confidence-label"
              fz="11px"
              lh="14px"
              c="light-dark(#868E96, var(--mantine-color-dark-2))"
              fw={700}
              tt="uppercase"
            >
              Confidence
            </Text>
          </ScoreRingStyle>
          <div id="ai-extraction-details-overview">
            <StatusPillStyle id="ai-extraction-details-status-pill" $tone={statusTone}>
              {(displayedStatus || 'unverified').replace('_', ' ')}
            </StatusPillStyle>
            <Text
              id="ai-extraction-details-verifier-summary"
              fz="14px"
              lh="22px"
              c="light-dark(#25262B, var(--mantine-color-dark-0))"
              fw={500}
              mt={12}
            >
              {sanitizeVerifierText(details.summary) ||
                'The extracted values were checked against the source sheet and field-level evidence is shown below.'}
            </Text>
            <Text
              id="ai-extraction-details-source"
              fz="12px"
              lh="18px"
              c="light-dark(#868E96, var(--mantine-color-dark-2))"
              mt={10}
            >
              {details.source?.file_name || record?.file_name || '-'} ·{' '}
              {details.source?.sheet_name || record?.sheet_name || '-'} ·{' '}
              {details.source?.category || record?.category || '-'}
            </Text>
          </div>
        </DetailsHeaderStyle>
        <DetailGridStyle id="ai-extraction-details-metrics">
          <DetailCardStyle id="ai-extraction-details-status">
            <Text
              id="ai-extraction-details-status-label"
              fz="11px"
              c="light-dark(#868E96, var(--mantine-color-dark-2))"
              tt="uppercase"
              fw={700}
            >
              Status
            </Text>
            <Text
              id="ai-extraction-details-status-value"
              fz="18px"
              c="light-dark(#25262B, var(--mantine-color-dark-0))"
              fw={700}
              tt="capitalize"
            >
              {(displayedStatus || '-').replace('_', ' ')}
            </Text>
          </DetailCardStyle>
          <DetailCardStyle id="ai-extraction-details-matched">
            <Text
              id="ai-extraction-details-matched-label"
              fz="11px"
              c="light-dark(#868E96, var(--mantine-color-dark-2))"
              tt="uppercase"
              fw={700}
            >
              Matched Fields
            </Text>
            <Text
              id="ai-extraction-details-matched-value"
              fz="18px"
              c="light-dark(#25262B, var(--mantine-color-dark-0))"
              fw={700}
            >
              {details.matched_fields ?? '-'} / {details.checked_fields ?? '-'}
            </Text>
          </DetailCardStyle>
          <DetailCardStyle id="ai-extraction-details-review">
            <Text
              id="ai-extraction-details-review-label"
              fz="11px"
              c="light-dark(#868E96, var(--mantine-color-dark-2))"
              tt="uppercase"
              fw={700}
            >
              Needs Review
            </Text>
            <Text
              id="ai-extraction-details-review-value"
              fz="18px"
              c="light-dark(#25262B, var(--mantine-color-dark-0))"
              fw={700}
            >
              {details.needs_review_fields ?? '-'}
            </Text>
          </DetailCardStyle>
        </DetailGridStyle>
        <SectionHeaderStyle id="ai-extraction-evidence-header">
          <Text
            id="ai-extraction-evidence-title"
            fz="15px"
            c="light-dark(#25262B, var(--mantine-color-dark-0))"
            fw={700}
          >
            Source Evidence
          </Text>
          <VerdictSummaryStyle id="ai-extraction-verdict-summary">
            {Object.entries(verdictCounts).map(([verdict, count]) => (
              <VerdictPillStyle
                id={`ai-extraction-verdict-${verdict}`}
                key={verdict}
                $tone={verdictTones[verdict]}
                $active={selectedVerdict === verdict}
                type="button"
                onClick={() => {
                  setSelectedVerdict(current => (current === verdict ? null : verdict));
                }}
              >
                {verdict.replace('_', ' ')}: {count}
              </VerdictPillStyle>
            ))}
          </VerdictSummaryStyle>
        </SectionHeaderStyle>
        {evidence.length > 0 && (
          <EvidenceFilterMetaStyle id="ai-extraction-evidence-filter-meta">
            {selectedVerdict
              ? `Showing ${filteredEvidence.length} of ${evidence.length} ${selectedVerdict.replace('_', ' ')} evidence rows. Click again to clear.`
              : `Showing all ${evidence.length} evidence rows.`}
          </EvidenceFilterMetaStyle>
        )}
        <EvidenceListStyle id="ai-extraction-evidence-list">
          {filteredEvidence.length > 0 ? (
            filteredEvidence.map(item => {
              const evidenceItemId = getEvidenceItemId(item);
              return (
                <EvidenceItemStyle
                  id={`ai-extraction-evidence-${evidenceItemId}`}
                  key={evidenceItemId}
                  $tone={verdictTones[item.verdict]}
                >
                  <EvidenceHeaderStyle id={`ai-extraction-evidence-field-${evidenceItemId}`}>
                    <Text
                      id={`ai-extraction-evidence-field-name-${evidenceItemId}`}
                      fz="14px"
                      c="light-dark(#25262B, var(--mantine-color-dark-0))"
                      fw={800}
                    >
                      {item.field}
                    </Text>
                    <EvidenceBadgeGroupStyle id={`ai-extraction-evidence-badges-${evidenceItemId}`}>
                      <EvidenceCategoryBadgeStyle
                        id={`ai-extraction-evidence-category-${evidenceItemId}`}
                      >
                        {item.extraction_category ||
                          details.source?.category ||
                          record?.category ||
                          '-'}
                      </EvidenceCategoryBadgeStyle>
                      <VerdictPillStyle
                        id={`ai-extraction-evidence-verdict-${evidenceItemId}`}
                        $tone={verdictTones[item.verdict]}
                        type="button"
                        disabled
                      >
                        {(item.verdict || '-').replace('_', ' ')}
                      </VerdictPillStyle>
                      <FieldConfidenceBadgeStyle
                        id={`ai-extraction-evidence-field-confidence-${evidenceItemId}`}
                        $score={getFieldConfidenceScore(item)}
                      >
                        {getFieldConfidenceScore(item).toFixed(1)}% field confidence
                      </FieldConfidenceBadgeStyle>
                    </EvidenceBadgeGroupStyle>
                  </EvidenceHeaderStyle>
                  <div id={`ai-extraction-evidence-values-${evidenceItemId}`}>
                    <EvidenceValueStyle id={`ai-extraction-evidence-extracted-${evidenceItemId}`}>
                      <Text
                        id={`ai-extraction-evidence-extracted-label-${evidenceItemId}`}
                        fz="11px"
                        c="light-dark(#868E96, var(--mantine-color-dark-2))"
                        fw={700}
                      >
                        Extracted
                      </Text>
                      <Text
                        id={`ai-extraction-evidence-extracted-value-${evidenceItemId}`}
                        fz="12px"
                        c="light-dark(#25262B, var(--mantine-color-dark-0))"
                        fw={700}
                      >
                        {item.extracted_value || '-'}
                      </Text>
                    </EvidenceValueStyle>
                    <EvidenceValueStyle id={`ai-extraction-evidence-source-${evidenceItemId}`}>
                      <Text
                        id={`ai-extraction-evidence-source-label-${evidenceItemId}`}
                        fz="11px"
                        c="light-dark(#868E96, var(--mantine-color-dark-2))"
                        fw={700}
                      >
                        Source
                      </Text>
                      <div id={`ai-extraction-evidence-source-value-${evidenceItemId}`}>
                        {renderSourceReference(item, evidenceItemId)}
                      </div>
                    </EvidenceValueStyle>
                  </div>
                  <EvidenceReasonStyle id={`ai-extraction-evidence-reason-${evidenceItemId}`}>
                    {item.reason || '-'}
                  </EvidenceReasonStyle>
                </EvidenceItemStyle>
              );
            })
          ) : (
            <Text
              id="ai-extraction-evidence-empty"
              fz="12px"
              c="light-dark(#868E96, var(--mantine-color-dark-2))"
            >
              {selectedVerdict
                ? `No ${selectedVerdict.replace('_', ' ')} source evidence rows.`
                : 'No field-level source evidence was returned by the verifier.'}
            </Text>
          )}
        </EvidenceListStyle>
        {concerns.length > 0 && (
          <>
            <Text
              id="ai-extraction-concerns-title"
              fz="13px"
              c="light-dark(#25262B, var(--mantine-color-dark-0))"
              fw={600}
              mt={16}
            >
              Review Notes
            </Text>
            <EvidenceListStyle id="ai-extraction-concerns-list">
              {concerns.map((concern, index) => (
                <NoteItemStyle id={`ai-extraction-concern-${index}`} key={`${concern}-${index}`}>
                  {concern}
                </NoteItemStyle>
              ))}
            </EvidenceListStyle>
          </>
        )}
        {methodology.length > 0 && (
          <>
            <Text
              id="ai-extraction-methodology-title"
              fz="13px"
              c="light-dark(#25262B, var(--mantine-color-dark-0))"
              fw={600}
              mt={16}
            >
              How This Was Verified
            </Text>
            <EvidenceListStyle id="ai-extraction-methodology-list">
              {methodology.map((method, index) => (
                <NoteItemStyle id={`ai-extraction-method-${index}`} key={`${method}-${index}`}>
                  {method}
                </NoteItemStyle>
              ))}
            </EvidenceListStyle>
          </>
        )}
      </DetailsModalBodyStyle>
    </Modal>
  );
};
